import PropTypes from 'prop-types';
import { Button } from '../ui/button';

export default function TemplateCard({
  template,
  userId,
  activeMode,
  isClinicAdmin,
  onEdit,
  onOpenVersions,
  onPreview,
  onOffer,
  onArchive,
}) {
  const isOwner = template.owner_type === 'user' ? template.owner_id === userId : isClinicAdmin;

  return (
    <article className="flex flex-col md:flex-row md:items-center justify-between p-4 border rounded-lg bg-white shadow-sm gap-4">
      <div className="space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-lg font-semibold">{template.name}</h2>
          <span className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded capitalize">
            {template.scope || template.owner_type}
          </span>
          {template.draft_id && (
            <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
              Draft available
            </span>
          )}
          {template.status === 'archived' && (
            <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">
              Archived
            </span>
          )}
        </div>
        {template.description && (
          <p className="text-sm text-slate-600">{template.description}</p>
        )}
        <div className="text-sm text-slate-500 space-y-0.5">
          <p>
            {template.active_version_number
              ? `Used for new assessments: Version ${template.active_version_number}`
              : 'Not selected for new assessments'}
          </p>
          {template.newest_published_version_number && template.newest_published_version_number !== template.active_version_number && (
            <p className="text-xs text-slate-400">
              Newest published: Version {template.newest_published_version_number}
            </p>
          )}
          {template.provenance?.source_template_id && (
            <p className="text-xs text-blue-600">
              Duplicated from {template.provenance.source_kind || 'template'} copy
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {isOwner && template.status !== 'archived' && (
          <Button aria-label={`Edit ${template.name}`} onClick={() => onEdit(template)}>
            Edit
          </Button>
        )}
        {/* Preview: visible to non-owners with an active version, or anyone */}
        {!isOwner && template.active_version_id && onPreview && (
          <Button
            aria-label={`Preview ${template.name}`}
            variant="outline"
            onClick={() => onPreview(template)}
          >
            Preview
          </Button>
        )}
        <Button
          aria-label={`Version history for ${template.name}`}
          variant="outline"
          onClick={() => onOpenVersions(template)}
        >
          Version history
        </Button>
        {/* Share copy with clinic — personal templates in clinic context */}
        {template.owner_type === 'user' &&
          template.active_version_id &&
          !['personal', 'everything'].includes(activeMode) &&
          isOwner &&
          onOffer && (
            <Button
              aria-label={`Share ${template.name} with clinic`}
              variant="outline"
              onClick={() => onOffer(template)}
            >
              Share copy with clinic
            </Button>
          )}
        {isOwner && template.status !== 'archived' && onArchive && (
          <Button
            aria-label={`Archive ${template.name}`}
            variant="outline"
            onClick={() => onArchive(template)}
          >
            Archive
          </Button>
        )}
      </div>
    </article>
  );
}

TemplateCard.propTypes = {
  template: PropTypes.object.isRequired,
  userId: PropTypes.string.isRequired,
  activeMode: PropTypes.string.isRequired,
  isClinicAdmin: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onOpenVersions: PropTypes.func.isRequired,
  onPreview: PropTypes.func,
  onOffer: PropTypes.func,
  onArchive: PropTypes.func,
};
